import React, { useState, useRef, useEffect } from "react";
import { BrowserMultiFormatReader } from "@zxing/library";
import Icon from "../AppIcon";
import Button from "./Button";

const BarcodeScanner = ({ onScan, onClose }) => {
  const [mode, setMode] = useState("camera"); // 'camera' or 'upload'
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    codeReaderRef.current = new BrowserMultiFormatReader();
    return () => {
      // Cleanup on unmount
      stopScanning();
    };
  }, []);

  const stopScanning = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
    }
    setIsScanning(false);
  };

  const startCameraScan = async () => {
    try {
      setError(null);
      setIsScanning(true);

      // Get available video input devices
      const videoInputDevices = await codeReaderRef.current.listVideoInputDevices();

      if (videoInputDevices.length === 0) {
        throw new Error("No camera found. Please connect a camera device.");
      }

      // Prefer a back / environment-facing camera on mobile if available
      const preferredBackCamera =
        videoInputDevices.find((device) =>
          /back|rear|environment|world/i.test(device.label || "")
        ) || videoInputDevices[videoInputDevices.length - 1];

      const selectedDeviceId = preferredBackCamera.deviceId;

      // Get user media stream
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: selectedDeviceId } }
      });
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }

      // Start decoding from video device
      codeReaderRef.current.decodeFromVideoDevice(
        selectedDeviceId,
        videoRef.current,
        (result, error) => {
          if (result) {
            const scannedText = result.getText();
            stopScanning();
            onScan(scannedText);
            if (onClose) onClose();
          }
          if (error && error.name !== "NotFoundException") {
            // NotFoundException is normal when no barcode is detected
            console.error("Scan error:", error);
          }
        }
      );
    } catch (err) {
      console.error("Camera scan error:", err);
      setError(err.message || "Failed to start camera. Please check permissions.");
      setIsScanning(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setError(null);
      setIsScanning(true);

      // Create image element
      const img = new Image();
      const imageUrl = URL.createObjectURL(file);

      img.onload = async () => {
        try {
          // Decode barcode from image
          const result = await codeReaderRef.current.decodeFromImage(img);
          const scannedText = result.getText();
          URL.revokeObjectURL(imageUrl);
          onScan(scannedText);
          if (onClose) onClose();
        } catch (err) {
          console.error("Image scan error:", err);
          setError("Could not detect barcode in image. Please try another image or use camera scan.");
          setIsScanning(false);
          URL.revokeObjectURL(imageUrl);
        }
      };

      img.onerror = () => {
        setError("Failed to load image. Please try another file.");
        setIsScanning(false);
        URL.revokeObjectURL(imageUrl);
      };

      img.src = imageUrl;
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.message || "Failed to process image.");
      setIsScanning(false);
    }
  };

  const handleClose = () => {
    stopScanning();
    if (onClose) onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            Scan Barcode
          </h3>
          <button
            onClick={handleClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Mode Toggle */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={mode === "camera" ? "default" : "outline"}
            size="sm"
            onClick={() => {
              stopScanning();
              setMode("camera");
              setError(null);
            }}
            className="flex-1"
          >
            <Icon name="Camera" size={16} className="mr-2" />
            Camera
          </Button>
          <Button
            variant={mode === "upload" ? "default" : "outline"}
            size="sm"
            onClick={() => {
              stopScanning();
              setMode("upload");
              setError(null);
            }}
            className="flex-1"
          >
            <Icon name="Upload" size={16} className="mr-2" />
            Upload Image
          </Button>
        </div>

        {/* Camera Mode */}
        {mode === "camera" && (
          <div className="space-y-4">
            <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
                muted
              />
              {!isScanning && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <p className="text-white text-sm">Camera feed will appear here</p>
                </div>
              )}
            </div>
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
            <div className="flex gap-2">
              {!isScanning ? (
                <Button
                  variant="default"
                  onClick={startCameraScan}
                  className="flex-1"
                  iconName="Camera"
                  iconPosition="left"
                >
                  Start Scanning
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={stopScanning}
                  className="flex-1"
                  iconName="Square"
                  iconPosition="left"
                >
                  Stop Scanning
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Upload Mode */}
        {mode === "upload" && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
              <Icon name="Upload" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground mb-4">
                Upload an image containing a barcode
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={isScanning}
                iconName="Image"
                iconPosition="left"
              >
                {isScanning ? "Processing..." : "Choose Image"}
              </Button>
            </div>
            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 text-xs text-muted-foreground text-center">
          Supported formats: Code128, Code39, EAN, UPC, QR Code, and more
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanner;
